import { useEffect, useMemo } from 'react';
import { atom, bind, sleep, withDataAtom, withErrorAtom, withRetry, isAtom, toAbortError, reatomResource, isDeepEqual, noop, isObject, } from '@reatom/framework';
import { useCtx } from '@reatom/npm-react';
import { onEvent } from '@reatom/web';
import { getComponentDebugName } from '@/js/libs/devtools';
import { Observable, createObservableFromAtom } from '@/js/libs/observable';
import { NotActive } from './notActive';
import { parseError } from './parseError';
import { useAbortOnUnmount } from './useAbortOnUnmount';
import { useRequestContextValue } from './UseRequestContext';
export const DEFAULT_QUERY_PARAMS = { page: 0, size: 250, limit: 250 };
export function createFetch(apiMethod, config, _ctx) {
    for (const [name, prop] of Object.entries(config)) {
        if (name !== 'name' && !isAtom(prop))
            throw new TypeError(`${name} should be an atom`);
    }
    const { debounce, params, onSuccess, onFail, abortSignal, getRetry, ignoreErrors, queryDefaults, name = apiMethod.name } = config;
    const memoParams = atom((ctx, state) => {
        const a = ctx.spy(params);
        const p = a instanceof Observable ? ctx.spy(a.__INTERNAL_DO_NOT_USE_ATOM__) : a;
        const paramsValue = isObject(p)
            ? Object.entries(p).reduce((acc, [key, value]) => {
                acc[key] = value instanceof Observable ? ctx.spy(value.__INTERNAL_DO_NOT_USE_ATOM__) : value;
                return acc;
            }, {})
            : p;
        return isDeepEqual(state, paramsValue) ? state : paramsValue;
    }, `${name}._memoParams`);
    const fetchAction = reatomResource(async (ctx) => {
        const p = ctx.spy(memoParams);
        // if (typeof p === 'function') p = await p(ctx);
        // abort error ignored by `withRetry` and `withErrorAtom`
        if (p === NotActive)
            throw toAbortError('not active');
        onEvent(ctx, ctx.get(abortSignal), 'abort', (event) => ctx.controller.abort(event.target?.reason));
        const aDebounce = ctx.get(debounce);
        if (aDebounce)
            await ctx.schedule(() => sleep(aDebounce));
        const response = await ctx.schedule(() => apiMethod({ params: p, signal: ctx.controller.signal, ignoreErrors: ctx.get(ignoreErrors), queryDefaults: ctx.get(queryDefaults) }));
        try {
            ctx.get(onSuccess)?.(response.data, p);
        }
        catch {
            // nothing. It is needing to ignore all errors in onSuccess
        }
        return response;
    }, name).pipe(withDataAtom(undefined, (_, response) => response.data), withRetry({
        onReject(ctx, e, retries) {
            const error = parseError(e, name);
            if (!error || error.status !== 403) {
                const getRetryFn = ctx.get(getRetry) ?? (() => (retries === 0 ? 1000 : undefined));
                return getRetryFn(ctx.get(fetchAction.errorAtom), retries);
            }
            return undefined;
        },
    }), withErrorAtom((ctx, e) => {
        const error = parseError(e, name);
        // safety call `onFail` before render
        if (error) {
            ctx.schedule(() => {
                const isSuppressed = ctx.get(ignoreErrors)?.includes(error.status);
                if (!isSuppressed) {
                    const isRetrying = ctx.get(fetchAction.retriesAtom) > 0;
                    ctx.get(onFail)?.(error, isRetrying);
                }
            });
        }
        return error;
    }));
    fetchAction.retriesAtom.__reatom.name = fetchAction.retriesAtom.__reatom.name?.replace('retriesAtom', '_retriesAtom');
    fetchAction.pendingAtom.__reatom.name = fetchAction.pendingAtom.__reatom.name?.replace('pendingAtom', '_pendingAtom');
    const isLoadingAtom = atom((ctx, state) => {
        const isActive = ctx.get(memoParams) !== NotActive;
        const isInit = state === undefined;
        const isPending = ctx.spy(fetchAction.pendingAtom) > 0;
        const isRetrying = ctx.spy(fetchAction.retriesAtom) > 0;
        return isActive && (isInit || isPending || isRetrying);
    }, `${name}.isLoadingAtom`);
    // ensure `isInit` state will handled correctly
    fetchAction.onCall((ctx) => ctx.get(isLoadingAtom));
    return {
        retry: async () => {
            await fetchAction.retry(_ctx);
        },
        data$: createObservableFromAtom(fetchAction.dataAtom),
        pending$: createObservableFromAtom(isLoadingAtom),
        error$: createObservableFromAtom(fetchAction.errorAtom),
        setData: bind(_ctx, fetchAction.dataAtom),
        fetchAction,
        init: () => _ctx.subscribe(fetchAction, noop),
    };
}
const defaultParameters = {};
export function useRequest(config) {
    const { onFail: onFailFromContext, debounce: debounceFromContext } = useRequestContextValue();
    const { 
    // Api method
    apiMethod, 
    // parameter, which will be passed to apiMethod
    params = defaultParameters, debounce = debounceFromContext, onSuccess, onFail = onFailFromContext, getRetry, ignoreErrors, queryDefaults = DEFAULT_QUERY_PARAMS, } = config;
    const ctx = useCtx();
    const abortSignal = useAbortOnUnmount();
    const name = getComponentDebugName(apiMethod.name);
    const { configAtoms, result } = useMemo(() => {
        const createFetchConfig = {
            debounce: atom(debounce),
            getRetry: atom(undefined),
            params: atom(params),
            onSuccess: atom(undefined),
            onFail: atom(undefined),
            abortSignal: atom(abortSignal),
            ignoreErrors: atom(ignoreErrors ?? null),
            queryDefaults: atom(queryDefaults),
            name,
        };
        const fetchReturn = createFetch(apiMethod, createFetchConfig, ctx);
        return { configAtoms: createFetchConfig, result: fetchReturn };
    }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []);
    const depsToInit = [ctx, result];
    useEffect(() => {
        if (onSuccess) {
            return result.init();
        }
        return noop;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, depsToInit);
    ctx.get(() => {
        const lastParams = ctx.get(configAtoms.params);
        if (!isDeepEqual(lastParams, params)) {
            configAtoms.params(ctx, params);
        }
        if (ctx.get(configAtoms.debounce) !== debounce) {
            configAtoms.debounce(ctx, debounce);
        }
        if (ctx.get(configAtoms.onSuccess) !== onSuccess) {
            configAtoms.onSuccess(ctx, () => onSuccess);
        }
        if (ctx.get(configAtoms.onFail) !== onFail) {
            configAtoms.onFail(ctx, () => onFail);
        }
        if (ctx.get(configAtoms.getRetry) !== getRetry) {
            configAtoms.getRetry(ctx, () => getRetry);
        }
    });
    return result;
}
